import { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Plus, Skull } from "lucide-react";
import { useCart } from "@/lib/cart";
import CartDrawer from "@/components/CartDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WishlistShare() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const { add, setOpen } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/wishlist/${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setMissing(true));
  }, [userId]);

  return (
    <div data-testid="wishlist-share-page" className="min-h-screen bg-void newsprint-grain">
      <header className="border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="wishlist-share-brand" className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <a
            href="/shop"
            data-testid="wishlist-share-shop-link"
            className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
          >
            <ArrowLeft size={14} /> The Crypt Shop
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
        {missing ? (
          <div className="text-center py-24">
            <Skull size={40} className="mx-auto text-crimson crimson-glow mb-6" />
            <h1 className="font-display text-2xl sm:text-3xl uppercase text-paper mb-3">THIS CRYPT IS SEALED</h1>
            <p className="font-type text-sm text-paper/60">That want-list link is dead or never existed.</p>
          </div>
        ) : !data ? (
          <p className="font-type text-sm text-paper/50 text-center py-24">Summoning the list...</p>
        ) : (
          <>
            <p className="font-marker text-bloodglow text-base sm:text-lg mb-3 -rotate-1 inline-block">
              a gift-giver's roadmap
            </p>
            <h1 data-testid="wishlist-share-title" className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-4">
              {data.name}&rsquo;S <span className="text-crimson">WANT-LIST</span>
            </h1>
            <p className="font-type text-sm text-paper/60 mb-12">
              Buy them one of these and earn their undying loyalty. Or keep it. We won't tell.
            </p>
            {data.items.length === 0 ? (
              <p className="font-type text-sm text-paper/50 py-10 text-center">The list is empty — they're either content or unreachable.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {data.items.map((p, i) => (
                  <motion.article
                    key={p.id}
                    data-testid={`wishlist-share-item-${p.id}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="border-2 border-border bg-pulp flex flex-col"
                  >
                    <div className="spotlight-frame aspect-[2/3] border-0">
                      <img src={p.cover} alt={`${p.title} cover`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <p className="font-impact uppercase tracking-wide text-sm text-paper">{p.title}</p>
                      <p className="font-courier text-[10px] uppercase tracking-widest text-cyanx mt-1">{p.format}</p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="font-marker text-yell text-lg">${p.price.toFixed(2)}</span>
                        <button
                          data-testid={`wishlist-share-add-${p.id}`}
                          onClick={() => {
                            add(p);
                            toast.success(`${p.title} rests in your coffin.`, { action: { label: "View Coffin", onClick: () => setOpen(true) } });
                          }}
                          className="flex items-center gap-1.5 font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-3 py-2 ink-shadow-sm transition-all duration-150 hover:bg-crimsonhot"
                        >
                          <Plus size={13} /> Gift It
                        </button>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <CartDrawer />
    </div>
  );
}
